import React, { useState, useEffect, useImperativeHandle } from 'react';
import EquipeDaEtapa, { equipeNoWorkflow } from '../../shared/EquipeDaEtapa';
import { Plus, Trash2, Beaker, FileDown } from 'lucide-react';
import { generateFIVClivagemPDF, generateFIVEmbryosPDF } from '../../../../utils/workflowPDF';
import { useFIVSession } from '../../../../hooks/useFIVSession';
import { useOPUSession } from '../../../../hooks/useOPUSession';
import BuscaDeAnimal from '../../shared/BuscaDeAnimal';
import NumerarEmbrioesModal from '../../modals/NumerarEmbrioesModal';
import { fetchAnimalsRgd } from '../../../../data/animals';
import ImportarDaEtapa from '../../shared/ImportarDaEtapa';
import SemenDaSessao from '../../shared/SemenDaSessao';
import { listSemenBatches, rotuloDaPartida } from '../../../../data/semen';
import { mesmaDoadora, doadorasQueFaltam, fivSubstituidaPelaOpu } from '../../../../utils/importarDaEtapa';
import { embrioesUsadosDaFIV } from '../../../../data/embryos';
import { moverLinha } from '../../../../utils/moverLinha';
import { useArrastarLinhas, AlcaDeArrastar } from '../../shared/useArrastarLinhas';
import {
  EMPTY_FIV_SESSION,
  EMPTY_FIV_DONOR,
  FIV_STAGE_FIELDS,
  FIV_DT_STAGE_FIELDS,
  FIV_VITRI_STAGE_FIELDS,
  FIV_DESC_STAGE_FIELDS,
  calculateCleavageRate,
} from '../../../../constants/workflowGranular';
import { newFivDonorId } from '../../../../data/fiv';
import {
  paraTE, congeladosDT, vitrificados, descartados, produzidoPelaDoadora,
} from '../../../../utils/calculations/producao';
import { MARK } from '../../../../constants';
import { ehDoadora, ehTouro } from '../../../../utils/animais';
import {
  perdasDaDoadoraFIV, descreverPerda, confirmarRemocao,
} from '../../../../utils/confirmarRemocao';

/**
 * Campo numérico das contagens (viáveis, clivados, destinos).
 *
 * FORA do corpo do FIVTab de propósito. Definido lá dentro, ele virava uma
 * função nova a cada render; o React trata identidade diferente como
 * componente diferente, desmonta o <input> e monta outro no lugar. O efeito
 * era o campo perder o foco a cada dígito digitado — dava para digitar um
 * número por vez e ter que clicar de novo.
 */
const NumInput = ({ value, onChange, max, className = '' }) => (
  <input
    type="number" min="0" max={max} placeholder="—"
    className={`w-full px-1 py-0.5 border border-slate-200 rounded text-xs text-center
                focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white ${className}`}
    value={value || ''}
    onChange={(e) => onChange(parseInt(e.target.value) || 0)}
  />
);

/**
 * O campo de texto dentro da tabela de doadoras.
 *
 * A tabela é densa — vinte linhas na tela, célula de meia altura —, e o campo
 * padrão de `BuscaDeAnimal` é o do formulário. Passar a classe daqui mantém a
 * densidade sem duplicar o componente.
 */
const CAMPO_DA_TABELA = 'w-full px-1.5 py-0.5 pr-5 border border-slate-200 rounded text-xs '
  + 'focus:outline-none focus:ring-1 focus:ring-blue-400';

/** Os quatro grupos que somam o produzido. Ver utils/calculations/producao.js. */
const CAMPOS_DE_PRODUCAO = {
  estagio: FIV_STAGE_FIELDS,
  dt:      FIV_DT_STAGE_FIELDS,
  vitri:   FIV_VITRI_STAGE_FIELDS,
  desc:    FIV_DESC_STAGE_FIELDS,
};

const FIVTab = React.forwardRef(({ service, workflowData, onUpdate, laboratoryId, clients = [] }, ref) => {
  const { loadFIVData, saveFIVData, error: saveError } = useFIVSession(laboratoryId);
  const { loadOPUData } = useOPUSession(laboratoryId);

  const [fivData, setFivData]         = useState(EMPTY_FIV_SESSION);
  // Guarda a doadora, não um booleano: numerar é por linha da FIV, e o modal
  // precisa saber de quem são os embriões.
  const [doadoraNumerando, setDoadoraNumerando] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const equipe = equipeNoWorkflow(workflowData, onUpdate, 'fiv');
  const [saveStatus, setSaveStatus]   = useState(null);

  // Partidas com saldo, e o que ESTA sessão já tinha gravado. O segundo existe
  // porque `saldo_doses` da view já vem descontado do que foi gravado antes —
  // sem devolvê-lo, reabrir a sessão mostraria o estoque descontado duas vezes.
  const [semenBatches, setSemenBatches]   = useState([]);
  const [dosesGravadas, setDosesGravadas] = useState({});

  // Fora do efeito porque o botão "Importar da OPU" usa as duas. Duplicar
  // era garantir que um dia divergissem da inicialização.
  const enrichWithRGD = async (donors) => {
    const ids = donors.map(d => d.donor_id).filter(Boolean);
    if (ids.length === 0) return donors;
    const animals = await fetchAnimalsRgd(ids);
    const rgdMap = {};
    (animals || []).forEach(a => { rgdMap[a.id] = a.rgd || null; });
    return donors.map(d => ({ ...d, rgd: d.donor_id ? (rgdMap[d.donor_id] || null) : null }));
  };

  const fetchOPUDonors = async () => {
    // 1. Tenta tabelas normalizadas via hook (mesma lógica do OPUTab)
    const opuData = await loadOPUData(service?.id);
    if (opuData?.donors?.length > 0) return opuData.donors;

    // 2. Fallback legado JSONB
    const legacy = (workflowData?.opu_data?.donors || service?.opu_data?.donors || [])
      .filter(d => d.animal_identifier || d.animal_id);
    return legacy;
  };

  /**
   * Catálogo de partidas, para o seletor.
   *
   * Tolerante a falha de propósito, e é seguro ser: o que o seletor mostra não
   * decide o que é gravado — o lançamento da sessão vem de `loadFIVData`. Sem
   * catálogo a lista fica vazia, nada se perde.
   */
  useEffect(() => {
    if (!laboratoryId) return;
    let cancelado = false;
    listSemenBatches(laboratoryId)
      .then(b => { if (!cancelado) setSemenBatches(b || []); })
      .catch(err => console.error('Erro ao carregar estoque de sêmen:', err));
    return () => { cancelado = true; };
  }, [laboratoryId]);

  // ── Inicialização ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isInitialized || !laboratoryId || !service?.id) return;

    const initializeData = async () => {
      const normalized = await loadFIVData(service?.id);

      // Antes de qualquer ramo: o que esta sessão já tinha baixado do estoque.
      const gravadas = {};
      (normalized?.session?.semen || []).forEach(s => {
        gravadas[s.semen_batch_id] = Number(s.doses) || 0;
      });
      setDosesGravadas(gravadas);

      // Só usa dados normalizados se houver doadoras com identificação real
      const hasMeaningfulDonors = normalized?.donors_processing?.some(
        d => d.donor_id || d.donor_identifier
      );
      if (hasMeaningfulDonors) {
        const enriched = await enrichWithRGD(normalized.donors_processing);
        setFivData({ ...normalized, donors_processing: enriched });
        setIsInitialized(true);
        return;
      }

      const existingSession = normalized?.session || service?.fiv_data?.session || workflowData?.fiv_data?.session;
      const existingDonors  = normalized?.donors_processing
        || service?.fiv_data?.donors_processing
        || workflowData?.fiv_data?.donors_processing || [];

      const opuDonors = await fetchOPUDonors();
      let donors_processing;

      if (opuDonors.length > 0) {
        const opuDonorsRGD = await enrichWithRGD(opuDonors.map(d => ({ ...d, donor_id: d.animal_id })));
        const opuRGDMap = {};
        opuDonorsRGD.forEach(d => { opuRGDMap[d.animal_id] = d.rgd || null; });

        const opuMapped = opuDonors.map(opuDonor => {
          const existing = existingDonors.find(d => mesmaDoadora(d, opuDonor));
          return {
            ...EMPTY_FIV_DONOR,
            id: existing?.id || opuDonor.id || newFivDonorId(), _nova: !existing,
            donor_id: opuDonor.animal_id,
            donor_identifier: opuDonor.animal_identifier,
            rgd: opuRGDMap[opuDonor.animal_id] || null,
            bull: existing?.bull || opuDonor.bull || '',
            oocytes_received: opuDonor.oocytes_recovered || 0,
            viable_oocytes: opuDonor.oocytes_viable || 0,
            from_opu: true,
            ...(existing ? {
              cleaved_oocytes: existing.cleaved_oocytes || 0,
              stage_bi: existing.stage_bi || 0, stage_bl: existing.stage_bl || 0,
              stage_bx: existing.stage_bx || 0, stage_bn: existing.stage_bn || 0,
              stage_be: existing.stage_be || 0,
              dest_fresh: existing.dest_fresh || 0,
              dest_dt: existing.dest_dt || 0, dest_vitri: existing.dest_vitri || 0,
              dest_dt_obs: existing.dest_dt_obs || '', dest_vitri_obs: existing.dest_vitri_obs || '',
              dest_desc: existing.dest_desc || 0,
              dest_desc_obs: existing.dest_desc_obs || '',
              ...Object.fromEntries(FIV_DESC_STAGE_FIELDS.map(
                f => [f.key, existing[f.key] || 0])),
              ...Object.fromEntries(FIV_VITRI_STAGE_FIELDS.map(
                f => [f.key, existing[f.key] || 0])),
              dest_dt_bi: existing.dest_dt_bi || 0, dest_dt_bl: existing.dest_dt_bl || 0,
              dest_dt_bx: existing.dest_dt_bx || 0, dest_dt_bn: existing.dest_dt_bn || 0,
              dest_dt_be: existing.dest_dt_be || 0,
            } : {})
          };
        });

        const manualDonors = existingDonors.filter(d =>
          (d.donor_id || d.donor_identifier) && // ignora linhas vazias salvas por bug anterior
          !opuDonors.some(o => mesmaDoadora(d, o))
        );
        donors_processing = [...opuMapped, ...manualDonors];
      } else if (existingDonors.length > 0) {
        donors_processing = existingDonors;
      } else {
        donors_processing = [{ ...EMPTY_FIV_DONOR, id: newFivDonorId(), _nova: true }];
      }

      setFivData({
        ...EMPTY_FIV_SESSION,
        session: existingSession || {
          reception_date: workflowData?.lab_date || service?.lab_scheduled_date || null,
          reception_time: null, embryologist_name: '',
          opu_link: service?.opu_data ? service.id : null
        },
        donors_processing,
        completed: normalized?.completed || service?.fiv_data?.completed || workflowData?.lab_done || false
      });
      setIsInitialized(true);
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, laboratoryId, isInitialized]);

  // ── Importar da OPU ─────────────────────────────────────────────────────

  /**
   * Linha em branco: a que a aba cria sozinha quando não há nada a mostrar.
   *
   * Sai quando a importação traz doadoras de verdade, senão fica um buraco no
   * meio da tabela. Qualquer coisa digitada — nome, touro, um número — a
   * segura: a linha vazia é a do sistema, não a de quem estava preenchendo.
   */
  const linhaEmBranco = (d) =>
    !d.donor_id && !d.donor_identifier && !d.bull && !d.bull_name &&
    !parseInt(d.oocytes_received) && !parseInt(d.viable_oocytes) &&
    !parseInt(d.cleaved_oocytes) &&
    FIV_STAGE_FIELDS.every(f => !parseInt(d[f.key]));

  /**
   * Traz da OPU as doadoras que ainda não estão aqui.
   *
   * Só acrescenta. A doadora que já existe não é tocada — nem os oócitos, nem
   * os estágios, nem os destinos —, porque o que está na FIV foi digitado por
   * quem estava olhando a placa, e a OPU não sabe mais que ele.
   *
   * A lançada à mão, que não existe na OPU, também fica.
   */
  const importarDaOPU = async () => {
    const opuDonors = await fetchOPUDonors();
    if (opuDonors.length === 0) return 0;

    const faltando = doadorasQueFaltam(fivData.donors_processing || [], opuDonors);
    if (faltando.length === 0) return 0;

    const comRGD = await enrichWithRGD(faltando.map(d => ({ ...d, donor_id: d.animal_id })));
    const rgdPorAnimal = {};
    comRGD.forEach(d => { rgdPorAnimal[d.animal_id] = d.rgd || null; });

    const novas = faltando.map(o => ({
      ...EMPTY_FIV_DONOR,
      id: newFivDonorId(),
      _nova: true,
      donor_id: o.animal_id,
      donor_identifier: o.animal_identifier,
      rgd: rgdPorAnimal[o.animal_id] || null,
      bull: o.bull || '',
      oocytes_received: o.oocytes_recovered || 0,
      viable_oocytes: o.oocytes_viable || 0,
      from_opu: true,
    }));

    setFivData(prev => ({
      ...prev,
      donors_processing: [
        ...(prev.donors_processing || []).filter(d => !linhaEmBranco(d)),
        ...novas,
      ],
    }));
    return novas.length;
  };

  /**
   * "Substituir pela OPU": descarta a FIV da tela e a refaz linha a linha da
   * OPU (regra em `fivSubstituidaPelaOpu`). Só na tela — grava no "Salvar".
   *
   * Bloqueia quando algum embrião desta FIV já foi usado: a doadora dele pode
   * sair na troca, e embrião transferido sem doadora de origem é estrago que
   * não se vê na hora.
   */
  const substituirPelaOPU = async () => {
    const usados = await embrioesUsadosDaFIV(service.id);
    if (usados > 0) {
      return { bloqueio: `${usados} embrião(ões) desta FIV já foram transferidos ou saíram. `
        + 'Substituir deixaria esses embriões sem doadora de origem.' };
    }
    const opuDonors = await fetchOPUDonors();
    if (opuDonors.length === 0) return { bloqueio: 'A OPU não tem doadoras lançadas.' };

    const atuais = fivData.donors_processing || [];
    const { linhas, saem } = fivSubstituidaPelaOpu(atuais, opuDonors, newFivDonorId);

    const num = (v) => parseInt(v) || 0;
    const soma = (campo) => atuais.reduce((t, d) => t + num(d[campo]), 0);
    const embrioes = atuais.reduce((t, d) => t + num(d.stage_bi) + num(d.stage_bl)
      + num(d.stage_bx) + num(d.stage_bn) + num(d.stage_be), 0);
    const perdas = [
      embrioes > 0 && `${embrioes} embrião(ões) lançados`,
      soma('cleaved_oocytes') > 0 && `${soma('cleaved_oocytes')} clivados`,
      saem.length > 0 && `${saem.length} linha(s) que a OPU não tem`
        + ` (${[...new Set(saem.map(d => d.donor_identifier).filter(Boolean))].slice(0, 4).join(', ')})`,
    ].filter(Boolean);
    const pergunta = [
      `Substituir as ${atuais.length} linhas da FIV pelas ${linhas.length} da OPU?`,
      '',
      perdas.length > 0 ? `Vai junto: ${perdas.join(', ')}.` : 'Nada lançado na FIV será perdido.',
      '',
      'Nada é gravado até "Salvar Workflow". Fechar sem salvar desfaz.',
    ].join('\n');
    if (!window.confirm(pergunta)) return null;

    const comRGD = await enrichWithRGD(linhas.map(l => ({ ...l })));
    setFivData(prev => ({
      ...prev,
      // O salvamento precisa saber: com tudo zerado, o caminho legado não
      // gravaria os zeros (ver useFIVSession).
      _substituida: true,
      donors_processing: comRGD.map(l => ({ ...EMPTY_FIV_DONOR, ...l })),
    }));
    return linhas.length;
  };

  // Arrastar a doadora para outra posição. A ordem do array é o sort_order.
  const arrastar = useArrastarLinhas((de, para) => setFivData(prev => ({
    ...prev,
    donors_processing: moverLinha(prev.donors_processing, de, para),
  })));

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAddDonor = () =>
    setFivData(prev => ({
      ...prev,
      donors_processing: [...prev.donors_processing, { ...EMPTY_FIV_DONOR, id: newFivDonorId(), _nova: true }]
    }));

  /**
   * Remove a linha da doadora — perguntando quando ha o que perder.
   *
   * A lixeira tem 14px e fica encostada na coluna de TE. Um toque errado num
   * celular apagava a doadora e, ao salvar, os embrioes dela perdiam o vinculo
   * com a linha da FIV (a FK e ON DELETE SET NULL): o embriao sobrevive, mas
   * fica orfao, sem doadora na origem. E o tipo de estrago que so aparece
   * semanas depois, no relatorio por doadora.
   *
   * Linha vazia sai sem pergunta — ver o porque em utils/confirmarRemocao.
   */
  const handleRemoveDonor = (index) => {
    const d = fivData.donors_processing[index];
    const embrioesNumerados = (d?.embryos || []).some(e => e.embryo_number != null);

    const pode = confirmarRemocao(descreverPerda({
      nome: d?.donor_identifier || d?.bull,
      perdas: perdasDaDoadoraFIV(d),
      aviso: embrioesNumerados
        ? 'Os embriões já numerados ficam sem doadora de origem.'
        : null,
    }));
    if (!pode) return;

    setFivData(prev => ({
      ...prev,
      donors_processing: prev.donors_processing.filter((_, i) => i !== index)
    }));
  };

  const handleDonorChange = (donorIndex, field, value) =>
    setFivData(prev => ({
      ...prev,
      donors_processing: prev.donors_processing.map((d, i) =>
        i === donorIndex ? { ...d, [field]: value } : d
      )
    }));

  const handleSessionChange = (field, value) =>
    setFivData(prev => ({ ...prev, session: { ...prev.session, [field]: value } }));

  /**
   * A doadora escolhida na busca.
   *
   * O bloco de busca inteiro — campo, lista, "+ Cadastrar" e o modal — estava
   * copiado aqui e na TE, com o estado morando no pai indexado por linha. Era
   * a cópia que `BuscaDeAnimal` já tinha substituído nas outras três abas, e
   * ela carregava dois defeitos que a original não tem: o modal de cadastro
   * abria sem o brinco digitado, e a lista da doadora não filtrava categoria.
   *
   * O que precisava mesmo ficar no pai é isto: o que fazer com quem foi
   * escolhido. Sem o `|| null` no RGD, trocar uma doadora com RGD por outra
   * sem deixava a linha com o RGD da vaca errada.
   */
  const escolherDoadora = (donorIndex, animal) => {
    setFivData(prev => ({
      ...prev,
      donors_processing: prev.donors_processing.map((d, i) => (i === donorIndex ? {
        ...d,
        donor_id: animal.id,
        donor_identifier: animal.identifier,
        rgd: animal.rgd || null,
      } : d)),
    }));
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  //
  // A conta mora em utils/calculations/producao.js, não aqui: a tela, o PDF e
  // o espelho `lab_quantity` precisam da MESMA. Quando ela vivia só neste
  // arquivo, o PDF deduzia o descarte por subtração e chegava a outro número.
  const getDonorTE       = (d) => paraTE(d, FIV_STAGE_FIELDS);
  const getDTStageSum    = (d) => congeladosDT(d, FIV_DT_STAGE_FIELDS);
  const getVitriStageSum = (d) => FIV_VITRI_STAGE_FIELDS.reduce((s, f) => s + (parseInt(d[f.key]) || 0), 0);
  const getDescStageSum  = (d) => descartados(d, FIV_DESC_STAGE_FIELDS);

  // Descarte AFIRMADO, não deduzido. Antes era a sobra — total menos os
  // destinos — e por isso um embrião ainda não destinado aparecia como
  // descartado. São coisas diferentes, e agora cada uma tem seu número.
  const getDonorDescarte  = (d) => getDescStageSum(d);
  const getDonorVitri     = (d) => vitrificados(d, FIV_VITRI_STAGE_FIELDS);
  const getDonorProduzido = (d) => produzidoPelaDoadora(d, CAMPOS_DE_PRODUCAO);

  /**
   * Os destinos derivados, do jeito que o banco e o PDF esperam receber.
   *
   * Um lugar só: salvar e imprimir usam a MESMA lista, senão o PDF mostra um
   * número e o banco guarda outro.
   *
   * `dest_fresh` passa a ser o espelho da soma dos estágios — não mais uma
   * sobra. É o que o PDF lê como "TE".
   */
  const comDestinos = (lista) => lista.map(d => ({
    ...d,
    dest_dt:    getDTStageSum(d),
    dest_vitri: getDonorVitri(d),
    dest_desc:  getDescStageSum(d),
    dest_fresh: getDonorTE(d),
  }));

  const getStats = () => {
    const totalOocytes = fivData.donors_processing.reduce((s, d) => s + (parseInt(d.oocytes_received) || 0), 0);
    const totalViable  = fivData.donors_processing.reduce((s, d) => s + (parseInt(d.viable_oocytes)  || 0), 0);
    const totalCleaved = fivData.donors_processing.reduce((s, d) => s + (parseInt(d.cleaved_oocytes) || 0), 0);
    const totalEmbryos = fivData.donors_processing.reduce((s, d) => s + getDonorProduzido(d), 0);
    const freshCount   = fivData.donors_processing.reduce((s, d) => s + getDonorTE(d), 0);
    const dtCount      = fivData.donors_processing.reduce((s, d) => s + getDTStageSum(d), 0);
    const vitriCount   = fivData.donors_processing.reduce((s, d) => s + getDonorVitri(d), 0);
    const descCount    = fivData.donors_processing.reduce((s, d) => s + getDescStageSum(d), 0);
    const blastRate    = totalViable > 0 ? Math.round((totalEmbryos / totalViable) * 100) : 0;
    return { totalOocytes, totalViable, totalCleaved, totalEmbryos, freshCount,
      frozenCount: dtCount + vitriCount, dtCount, vitriCount, descCount, blastRate,
      cleavageRate: calculateCleavageRate(totalCleaved, totalViable) };
  };

  useEffect(() => {
    if (!isInitialized) return;
    const stats = getStats();
    onUpdate('lab_date', fivData.session.reception_date);
    onUpdate('lab_done', fivData.completed);
    onUpdate('lab_quantity', stats.totalEmbryos);
    // As contagens de congelamento seguem o mesmo caminho de lab_quantity:
    // denormalizadas no serviço para o cálculo do valor continuar síncrono,
    // sem ir ao banco no meio da conta do faturamento.
    onUpdate('qtd_vitrificados', stats.vitriCount);
    onUpdate('qtd_dt', stats.dtCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fivData, isInitialized]);

  // ── Save: computa dest_dt a partir da soma dos estágios ────────────────────
  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!isInitialized || !service?.id) return;
      // Não salva se não há doadoras com dados reais (evita envenenar sessão futura)
      const hasData = fivData.donors_processing.some(d => d.donor_id || d.donor_identifier || (parseInt(d.oocytes_received) || 0) > 0);
      if (!hasData) return Promise.resolve();
      const fivDataComputed = {
        ...fivData,
        donors_processing: comDestinos(fivData.donors_processing),
      };
      const result = await saveFIVData(service.id, fivDataComputed);
      setSaveStatus(result ? 'ok' : 'error');
      if (result) setFivData(prev => (prev._substituida ? { ...prev, _substituida: false } : prev));
      return result;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isInitialized, fivData, service?.id]);

  const stats   = getStats();
  const donors  = fivData.donors_processing;

  /**
   * As palhetas que a doadora pode ter usado são as descongeladas NESTA sessão
   * — não o estoque inteiro. Oócito não é fecundado com palheta que ficou no
   * botijão, e oferecer o catálogo todo aqui deixaria registrar exatamente
   * isso.
   */
  const partidaPorId   = Object.fromEntries(semenBatches.map(b => [b.semen_batch_id, b]));
  const partidasDaSessao = (fivData.session.semen || [])
    .map(s => partidaPorId[s.semen_batch_id])
    .filter(Boolean);

  /**
   * Declara a palheta desta linha no bloco da sessão, sem obrigar a subir lá.
   *
   * O ovo e a galinha que deixou o estoque parado: a coluna da partida só
   * lista o que a SESSÃO declarou, e enquanto nada é declarado ela mostra um
   * traço com a explicação escondida num `title`. Resultado em produção: 181
   * linhas de FIV com o touro digitado à mão, nenhuma ligada a uma partida, e
   * nenhuma dose baixada desde que o estoque existe.
   *
   * O botão não inverte a regra — a sessão continua sendo quem declara o que
   * foi descongelado, e a gota continua só rastreando. Ele encurta o caminho:
   * acha em estoque a partida do touro que já está na linha, declara uma
   * palheta e liga a doadora a ela. Sem partida daquele touro, abre uma linha
   * em branco no bloco para a pessoa escolher, em vez de não fazer nada.
   */
  const declararPalhetaDaLinha = (donorIndex) => {
    setFivData(prev => {
      const doadora = prev.donors_processing[donorIndex];
      const touro = (doadora?.bull || doadora?.bull_name || '').trim().toUpperCase();
      const atuais = prev.session.semen || [];

      const candidata = touro ? semenBatches.find(b =>
        (b.touro || '').trim().toUpperCase() === touro
        && Number(b.saldo_doses) > 0
        && !b.descartado_em) : null;

      if (!candidata) {
        return { ...prev, session: { ...prev.session,
          semen: [...atuais, { semen_batch_id: '', doses: 1, observacoes: '' }] } };
      }

      const jaDeclarada = atuais.some(s => s.semen_batch_id === candidata.semen_batch_id);
      return {
        ...prev,
        session: { ...prev.session, semen: jaDeclarada ? atuais
          : [...atuais, { semen_batch_id: candidata.semen_batch_id, doses: 1, observacoes: '' }] },
        donors_processing: prev.donors_processing.map((d, i) =>
          i === donorIndex ? { ...d, semen_batch_id: candidata.semen_batch_id } : d),
      };
    });
  };

  // Doadoras com touro lançado enquanto a sessão não declarou palheta nenhuma.
  // É exatamente o estado em que o estoque não baixa, e ele era invisível.
  const doadorasComTouro = (fivData.donors_processing || [])
    .filter(d => (d.bull || d.bull_name || '').trim()).length;
  const sessaoSemPalheta = (fivData.session.semen || []).length === 0;

  // Input numérico compacto
  return (
    <div className="p-4 space-y-5">

      {/* ── Cabeçalho da sessão ── */}
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Beaker className="w-4 h-4 text-slate-900" />
            <h4 className="font-semibold text-slate-900 text-sm">Sessão de Laboratório</h4>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Data Recepção *</label>
            <input type="date"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={fivData.session.reception_date || ''}
              onChange={(e) => handleSessionChange('reception_date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Horário</label>
            <input type="time"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={fivData.session.reception_time || ''}
              onChange={(e) => handleSessionChange('reception_time', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Embriologista</label>
            <input type="text" placeholder="Nome do embriologista"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={fivData.session.embryologist_name || ''}
              onChange={(e) => handleSessionChange('embryologist_name', e.target.value)}
            />
          </div>
          {/* A FIV não tem técnico na sessão: a equipe vive só no serviço. */}
          <EquipeDaEtapa
            laboratoryId={laboratoryId}
            responsavel={equipe.planejado}
            ajudante={equipe.ajudante}
            obrigatorio={false}
            onResponsavel={equipe.responsavelNaEquipe}
            onAjudante={equipe.ajudanteNaEquipe}
          />
        </div>
      </div>

      {/* ── Sêmen: as palhetas que baixam o estoque ── */}
      <SemenDaSessao
        batches={semenBatches}
        semen={fivData.session.semen || []}
        dosesGravadas={dosesGravadas}
        clientId={service?.client_id}
        clientName={service?.client_name}
        onChange={(lista) => handleSessionChange('semen', lista)}
      />

      {/* O estado em que o estoque não baixa, dito em voz alta. Ele era
          invisível: o touro ia digitado na linha, a coluna da partida ficava
          num traço, e ninguém tinha como saber que o botijão seguia devendo. */}
      {sessaoSemPalheta && doadorasComTouro > 0 && (
        <p className="-mt-3 px-3 text-xs text-slate-600">
          {MARK.WARN} {doadorasComTouro} doadora(s) com touro lançado e nenhuma palheta
          declarada aqui. O estoque só baixa pela palheta — use o <span className="font-medium">+ palheta</span> na
          coluna Partida da doadora, ou lance acima.
        </p>
      )}

      {/* ── Stats rápidas ── */}
      <div className="p-3 bg-white border border-slate-200 rounded-lg">
        <div className="grid grid-cols-6 gap-3 text-xs">
          <div><p className="text-slate-500">Óvulos Rec.</p><p className="font-semibold text-slate-900 text-sm">{stats.totalOocytes}</p></div>
          <div><p className="text-slate-500">Viáveis</p><p className="font-semibold text-slate-900 text-sm">{stats.totalViable}</p></div>
          <div>
            <p className="text-slate-500">Clivados D2</p>
            <p className={`font-semibold text-sm ${stats.totalCleaved > 0 ? 'text-slate-900' : 'text-slate-400'}`}>
              {stats.totalCleaved > 0 ? `${stats.totalCleaved} (${stats.cleavageRate}%)` : '—'}
            </p>
          </div>
          <div><p className="text-slate-500">Embriões D7</p><p className="font-semibold text-slate-900 text-sm">{stats.totalEmbryos}</p></div>
          <div><p className="text-slate-500">Taxa Blasto.</p><p className="font-semibold text-slate-900 text-sm">{stats.blastRate}%</p></div>
          <div>
            <p className="text-slate-500">TE / Cong.</p>
            <p className="font-semibold text-blue-600 text-sm">
              {stats.freshCount} / {stats.frozenCount}
              {stats.dtCount > 0 && stats.vitriCount > 0 && (
                <span className="text-slate-400 font-normal"> (DT {stats.dtCount} + V {stats.vitriCount})</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          TABELA 1 — Produzido e transferido
      ═══════════════════════════════════════════════════════════════════════ */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-800 text-sm">Produzido e transferido</h4>
          <div className="flex items-center gap-2">
            <ImportarDaEtapa
              rotulo="Importar da OPU"
              fonte="da OPU"
              singular="doadora trazida"
              plural="doadoras trazidas"
              onImportar={importarDaOPU}
              onSubstituir={substituirPelaOPU}
              rotuloSubstituir="Substituir pela OPU"
            />
            <button onClick={() => generateFIVClivagemPDF(service, { ...fivData, donors_processing: comDestinos(donors) })}
              className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors">
              <FileDown className="w-3.5 h-3.5" /> Clivagem
            </button>
            <button onClick={() => generateFIVEmbryosPDF(service, { ...fivData, donors_processing: comDestinos(donors) })}
              className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors">
              <FileDown className="w-3.5 h-3.5" /> Embriões
            </button>
          </div>
        </div>

        <div className="overflow-x-auto sm:overflow-visible sm:w-max sm:min-w-full rounded-lg border border-slate-200">
          <table className="w-full text-xs border-collapse min-w-max">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200">
                <th colSpan={2} className="px-2 py-1 text-center text-slate-500 font-medium border-r border-slate-200">Identificação</th>
                <th colSpan={2} className="px-2 py-1 text-center text-slate-500 font-medium border-r border-slate-200">Sêmen</th>
                <th colSpan={4} className="px-2 py-1 text-center text-slate-700 font-medium border-r border-slate-200">Oócitos</th>
                <th colSpan={6} className="px-2 py-1 text-center text-slate-700 font-medium border-r border-slate-200">Estágios D7 — vão para a TE</th>
                <th className="px-2 py-1 text-center text-slate-700 font-medium border-r border-slate-200">Total</th>
                <th className="px-2 py-1"></th>
              </tr>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">Doadora</th>
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap border-r border-slate-200">Touro</th>
                <th className="px-2 py-1.5 text-center font-semibold w-28">Partida</th>
                <th className="px-2 py-1.5 text-center font-semibold w-14 border-r border-slate-200">Gotas</th>
                <th className="px-2 py-1.5 text-center font-semibold w-14">Viáveis</th>
                <th className="px-2 py-1.5 text-center font-semibold w-14">Total</th>
                <th className="px-2 py-1.5 text-center font-semibold w-14">Cliv. D2</th>
                <th className="px-2 py-1.5 text-center font-semibold w-14 border-r border-slate-200">Taxa%</th>
                <th className="px-2 py-1.5 text-center font-semibold w-16">BI</th>
                <th className="px-2 py-1.5 text-center font-semibold w-16">BL</th>
                <th className="px-2 py-1.5 text-center font-semibold w-16">BX</th>
                <th className="px-2 py-1.5 text-center font-semibold w-16">BN</th>
                <th className="px-2 py-1.5 text-center font-semibold w-16">BE</th>
                <th className="px-2 py-1.5 text-center font-semibold w-16 border-r border-slate-200 text-slate-700"
                    title="Soma dos cinco estágios. É o que segue para a transferência — o congelado e o descarte estão nas tabelas de baixo e se SOMAM a este número">
                  Para TE
                </th>
                <th className="px-2 py-1.5 text-center font-semibold w-16 border-r border-slate-200 text-slate-900"
                    title="Produzido = para TE + congelado (DT e vitrificação) + descarte">
                  Produzido
                </th>
                <th className="px-2 py-1.5 w-6"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {donors.map((donor, di) => {
                const total     = getDonorTE(donor);
                const produzido = getDonorProduzido(donor);
                const cliv      = calculateCleavageRate(donor.cleaved_oocytes, donor.viable_oocytes);

                const { realce, ...linhaArrastavel } = arrastar.linha(di);
                return (
                  <tr key={donor.id} {...linhaArrastavel} className={`hover:bg-slate-50 ${realce}`}>
                    {/* Doadora - autocomplete. A alça ⋮⋮ muda a ordem; vale
                        para as três tabelas, que são a mesma lista. */}
                    <td className="px-2 py-1 relative" style={{ minWidth: '130px' }}>
                      <div className="flex items-center gap-1">
                      <AlcaDeArrastar props={arrastar.alca(di, donors.length)} rotulo="doadora" />
                      <div className="flex-1 min-w-0">
                      {donor.from_opu ? (
                        <div>
                          <span className="font-semibold text-slate-800">{donor.donor_identifier}</span>
                          {donor.rgd && <span className="text-slate-400 ml-1">| {donor.rgd}</span>}
                        </div>
                      ) : (
                        <BuscaDeAnimal
                          laboratoryId={laboratoryId}
                          valor={donor.donor_identifier}
                          onDigitar={(t) => handleDonorChange(di, 'donor_identifier', t)}
                          onEscolher={(animal) => escolherDoadora(di, animal)}
                          filtro={ehDoadora}
                          categoriaPadrao="Doadora"
                          clients={clients}
                          clientIdPadrao={service?.client_id}
                          inputClassName={`${CAMPO_DA_TABELA} font-semibold`}
                        />
                      )}
                      </div>
                      </div>
                    </td>
                    {/* Touro */}
                    <td className="px-2 py-1 border-r border-slate-100 relative" style={{ minWidth: '120px' }}>
                      <BuscaDeAnimal
                        laboratoryId={laboratoryId}
                        valor={donor.bull}
                        onDigitar={(t) => handleDonorChange(di, 'bull', t)}
                        onEscolher={(touro) => handleDonorChange(di, 'bull', touro.identifier)}
                        filtro={ehTouro}
                        categoriaPadrao="Reprodutor"
                        clients={clients}
                        clientIdPadrao={service?.client_id}
                        inputClassName={CAMPO_DA_TABELA}
                      />
                    </td>
                    {/* Partida de sêmen — só as descongeladas nesta sessão */}
                    <td className="px-1.5 py-1">
                      {partidasDaSessao.length === 0 ? (
                        // Botão, não traço. O traço com a explicação num
                        // `title` é a razão de o estoque nunca ter baixado.
                        <button
                          type="button"
                          onClick={() => declararPalhetaDaLinha(di)}
                          title="Declara a palheta desta doadora no bloco Sêmen da sessão. É a palheta que baixa o estoque."
                          className="w-full px-1 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded border border-dashed border-blue-300 whitespace-nowrap"
                        >
                          + palheta
                        </button>
                      ) : (
                        <select
                          className="w-full px-1 py-0.5 border border-slate-200 rounded text-xs bg-white
                                     focus:outline-none focus:ring-1 focus:ring-blue-400"
                          value={donor.semen_batch_id || ''}
                          onChange={(e) => handleDonorChange(di, 'semen_batch_id', e.target.value || null)}
                        >
                          <option value="">—</option>
                          {partidasDaSessao.map(p => (
                            <option key={p.semen_batch_id} value={p.semen_batch_id}>
                              {rotuloDaPartida(p)}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    {/* Gotas — rastreia, não baixa estoque */}
                    <td className="px-1.5 py-1 border-r border-slate-100">
                      <div className="flex items-center gap-0.5">
                        <NumInput value={donor.gotas} onChange={(v) => handleDonorChange(di, 'gotas', v || null)} />
                        {/* Palheta de um touro e doadora fecundada com outro é
                            quase sempre linha trocada — avisa sem impedir. */}
                        {donor.semen_batch_id && donor.bull
                          && partidaPorId[donor.semen_batch_id]
                          && (partidaPorId[donor.semen_batch_id].touro || '').trim().toUpperCase()
                             !== (donor.bull || '').trim().toUpperCase() && (
                          <span title={`A partida é de ${partidaPorId[donor.semen_batch_id].touro}, mas o touro da linha é ${donor.bull}`}>
                            {MARK.WARN}
                          </span>
                        )}
                      </div>
                    </td>
                    {/* Viáveis */}
                    <td className="px-2 py-1">
                      {donor.from_opu
                        ? <span className="block text-center text-slate-700 font-medium">{donor.viable_oocytes || 0}</span>
                        : <NumInput value={donor.viable_oocytes} onChange={(v) => handleDonorChange(di, 'viable_oocytes', v)} />
                      }
                    </td>
                    {/* Total oócitos */}
                    <td className="px-2 py-1">
                      {donor.from_opu
                        ? <span className="block text-center text-slate-500">{donor.oocytes_received || 0}</span>
                        : <NumInput value={donor.oocytes_received} onChange={(v) => handleDonorChange(di, 'oocytes_received', v)} />
                      }
                    </td>
                    {/* Clivagem D2 */}
                    <td className="px-1.5 py-1">
                      <NumInput value={donor.cleaved_oocytes} onChange={(v) => handleDonorChange(di, 'cleaved_oocytes', v)} />
                    </td>
                    {/* Taxa% */}
                    <td className="px-2 py-1 text-center border-r border-slate-100">
                      {donor.viable_oocytes > 0 && donor.cleaved_oocytes > 0 ? (
                        <span className="font-semibold text-slate-900">{cliv}%{cliv < 70 && <span title="Abaixo da meta de 70%"> {MARK.WARN}</span>}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    {/* Estágios */}
                    {FIV_STAGE_FIELDS.map(({ key }) => (
                      <td key={key} className="px-1.5 py-1">
                        <NumInput value={donor[key]} onChange={(v) => handleDonorChange(di, key, v)} />
                      </td>
                    ))}
                    {/* Vão para TE + numerar */}
                    <td className="px-2 py-1 border-r border-slate-100 text-center">
                      <span className={`font-semibold ${total > 0 ? 'text-slate-700' : 'text-slate-300'}`}>
                        {total || '—'}
                      </span>
                      {/* `total`, não `produzido`: numera-se o que vai para a
                          TE. Doadora que só congelou não tem o que numerar —
                          a palheta já está fechada no botijão.

                          Doadora ainda não gravada não tem embrião no banco:
                          o modal abriria vazio e pareceria defeito. `_nova`
                          é o sinal de que ela só existe na tela — o id já não
                          serve para isso, porque nasce definitivo. O
                          `temp-` segue na conta pela doadora que ficou numa
                          aba aberta antes desta versão. */}
                      {total > 0 && !donor._nova && !String(donor.id || '').startsWith('temp-') && (
                        <button
                          type="button"
                          onClick={() => setDoadoraNumerando(donor)}
                          className="block w-full text-[11px] text-blue-600 hover:text-blue-800 underline mt-0.5"
                        >
                          numerar
                        </button>
                      )}
                    </td>
                    {/* Produzido: TE + congelado + descarte.
                        Fica ao lado do de TE de propósito. Enquanto havia só
                        um número aqui, ninguém tinha como ver que os
                        congelados de baixo não estavam sendo contados. */}
                    <td className="px-2 py-1 border-r border-slate-100 text-center">
                      <span className={`font-bold ${produzido > 0 ? 'text-slate-900' : 'text-slate-300'}`}
                            title={produzido > total
                              ? `${total} para TE + ${produzido - total} congelado/descarte`
                              : 'Soma dos quatro grupos'}>
                        {produzido || '—'}
                      </span>
                    </td>
                    {/* Remover */}
                    <td className="px-1.5 py-1 text-center">
                      <button onClick={() => handleRemoveDonor(di)}
                        className="text-slate-300 hover:text-red-600 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {donors.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-slate-700 text-xs">
                  <td colSpan={2} className="px-2 py-1.5 text-slate-500">Totais</td>
                  <td className="px-1.5 py-1.5"></td>
                  <td className="px-1.5 py-1.5 text-center border-r border-slate-200">
                    {donors.reduce((s, d) => s + (parseInt(d.gotas) || 0), 0) || '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center">{stats.totalViable}</td>
                  <td className="px-2 py-1.5 text-center">{stats.totalOocytes}</td>
                  <td className="px-2 py-1.5 text-center">{stats.totalCleaved || '—'}</td>
                  <td className="px-2 py-1.5 text-center border-r border-slate-200">
                    {stats.cleavageRate > 0
                      ? <span className="text-slate-900">{stats.cleavageRate}%{stats.cleavageRate < 70 && <span title="Abaixo da meta de 70%"> {MARK.WARN}</span>}</span>
                      : '—'}
                  </td>
                  {FIV_STAGE_FIELDS.map(({ key }) => (
                    <td key={key} className="px-1.5 py-1.5 text-center">
                      {donors.reduce((s, d) => s + (parseInt(d[key]) || 0), 0) || '—'}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center border-r border-slate-200 text-slate-700 font-bold">{stats.freshCount || '—'}</td>
                  <td className="px-2 py-1.5 text-center border-r border-slate-200 text-slate-900 font-bold">{stats.totalEmbryos || '—'}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <button onClick={handleAddDonor}
          className="mt-2 flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-xs transition-colors px-1">
          <Plus className="w-3.5 h-3.5" /> Adicionar doadora
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          TABELA 2 — Congelamento (DT e Vitrificação)
      ═══════════════════════════════════════════════════════════════════════ */}
      <div>
        <h4 className="font-semibold text-slate-800 text-sm mb-2">Congelamento</h4>
        <div className="overflow-x-auto sm:overflow-visible sm:w-max sm:min-w-full rounded-lg border border-slate-200">
          <table className="w-full text-xs border-collapse min-w-max">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-1 text-center text-slate-500 font-medium border-r border-slate-200">Doadora</th>
                <th colSpan={7} className="px-2 py-1 text-center text-blue-700 font-medium border-r border-slate-200">DT – Direct Transfer</th>
                <th colSpan={7} className="px-2 py-1 text-center text-slate-700 font-medium">Vitrificação</th>
              </tr>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap border-r border-slate-200">Doadora × Touro</th>
                {FIV_DT_STAGE_FIELDS.map(({ label }) => (
                  <th key={label} className="px-2 py-1.5 text-center font-semibold w-16 text-blue-700">{label}</th>
                ))}
                <th className="px-2 py-1.5 text-center font-semibold w-16 text-blue-700">Total</th>
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap border-r border-slate-200 text-blue-700">Local do DT</th>
                {FIV_VITRI_STAGE_FIELDS.map(({ label }) => (
                  <th key={label} className="px-2 py-1.5 text-center font-semibold w-16 text-slate-700">{label}</th>
                ))}
                <th className="px-2 py-1.5 text-center font-semibold w-16 text-slate-700">Total</th>
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap text-slate-700">Local da vitrificação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {donors.map((donor, di) => {
                const dtTotal    = getDTStageSum(donor);
                const vitriTotal = getDonorVitri(donor);
                return (
                  <tr key={donor.id} className="hover:bg-slate-50">
                    {/* Doadora × Touro (display) */}
                    <td className="px-2 py-1 border-r border-slate-100">
                      <span className="font-medium text-slate-800">{donor.donor_identifier || '—'}</span>
                      {donor.rgd && <span className="text-slate-400 ml-1 text-xs">| {donor.rgd}</span>}
                      {donor.bull && <span className="text-slate-400 ml-1">× {donor.bull}</span>}
                    </td>
                    {/* DT por estágio.
                        SEM `max`. O teto era a soma dos estágios de cima, o
                        que só fazia sentido quando o congelado saía de lá.
                        Como não sai, o teto só impedia o lançamento certo:
                        doadora que congelou tudo e não mandou nada para a TE
                        não conseguia digitar nada aqui. */}
                    {FIV_DT_STAGE_FIELDS.map(({ key }) => (
                      <td key={key} className="px-1.5 py-1">
                        <NumInput value={donor[key]}
                          onChange={(v) => handleDonorChange(di, key, v)}
                          className="focus:ring-blue-400 focus:border-blue-400"
                        />
                      </td>
                    ))}
                    {/* Total DT */}
                    <td className="px-2 py-1 text-center">
                      <span className={`font-semibold ${dtTotal > 0 ? 'text-blue-700' : 'text-slate-300'}`}>
                        {dtTotal || '—'}
                      </span>
                    </td>
                    {/* Local do DT — cada destino com o SEU campo.
                        Havia um só, preso a `dest_dt_obs`, servindo aos dois:
                        quem vitrificava e anotava o botijão escrevia no campo
                        do DT, e como o DT era zero o texto nunca chegava ao
                        embrião. `dest_vitri_obs` só era lido, nunca escrito. */}
                    <td className="px-2 py-1 border-r border-slate-100">
                      <input type="text" placeholder={dtTotal > 0 ? 'Botijão, caneca…' : '—'}
                        className="w-full min-w-[150px] px-1.5 py-0.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                        value={donor.dest_dt_obs || ''}
                        onChange={(e) => handleDonorChange(di, 'dest_dt_obs', e.target.value)}
                      />
                    </td>
                    {/* Vitrificação por estágio. Era uma caixinha de
                        quantidade só; sem o estágio, o embrião nascia sem
                        fase — e antes disso nem nascia. */}
                    {FIV_VITRI_STAGE_FIELDS.map(({ key }) => (
                      <td key={key} className="px-1.5 py-1">
                        <NumInput value={donor[key]}
                          onChange={(v) => handleDonorChange(di, key, v)}
                        />
                      </td>
                    ))}
                    {/* Total vitrificado */}
                    <td className="px-2 py-1 text-center">
                      <span className={`font-semibold ${vitriTotal > 0 ? 'text-slate-700' : 'text-slate-300'}`}
                            title={getVitriStageSum(donor) === 0 && vitriTotal > 0
                              ? 'Lançamento antigo, sem estágio anotado. Preencha as caixas ao lado para registrar a fase.'
                              : undefined}>
                        {vitriTotal || '—'}
                        {getVitriStageSum(donor) === 0 && vitriTotal > 0 && (
                          <span className="text-slate-400 ml-0.5">*</span>
                        )}
                      </span>
                    </td>
                    {/* Local da vitrificação */}
                    <td className="px-2 py-1">
                      <input type="text" placeholder={(parseInt(donor.dest_vitri) || 0) > 0 ? 'Botijão, caneca…' : '—'}
                        className="w-full min-w-[150px] px-1.5 py-0.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-slate-400"
                        value={donor.dest_vitri_obs || ''}
                        onChange={(e) => handleDonorChange(di, 'dest_vitri_obs', e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {donors.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-slate-700 text-xs">
                  <td className="px-2 py-1.5 text-slate-500 border-r border-slate-200">Totais</td>
                  {FIV_DT_STAGE_FIELDS.map(({ key }) => (
                    <td key={key} className="px-1.5 py-1.5 text-center text-blue-700">
                      {donors.reduce((s, d) => s + (parseInt(d[key]) || 0), 0) || '—'}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center text-blue-700 font-bold">{stats.dtCount || '—'}</td>
                  <td className="border-r border-slate-200"></td>
                  {FIV_VITRI_STAGE_FIELDS.map(({ key }) => (
                    <td key={key} className="px-1.5 py-1.5 text-center text-slate-700">
                      {donors.reduce((s, d) => s + (parseInt(d[key]) || 0), 0) || '—'}
                    </td>
                  ))}
                  <td className="px-1.5 py-1.5 text-center text-slate-700 font-bold">{stats.vitriCount || '—'}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Não há mais teto a validar: congelado e descarte não saem do bolo
            de cima, então nenhuma soma daqui pode "passar" do que foi
            produzido — ela É parte do que foi produzido.

            O que vale avisar é o contrário: congelado sem estágio, que é o
            lançamento antigo esperando a fase. */}
        {donors.some(d => getDonorVitri(d) > 0 && getVitriStageSum(d) === 0) && (
          <p className="mt-1 text-xs text-slate-500">
            <span className="text-slate-400">*</span> Vitrificado lançado antes
            da quebra por estágio existir. O embrião está no botijão e conta no
            produzido; falta só dizer em que fase ele foi congelado.
          </p>
        )}

        {stats.totalEmbryos > 0 && (
          <p className="mt-1 text-xs text-slate-500">
            <strong>{stats.totalEmbryos}</strong> produzidos ={' '}
            <strong>{stats.freshCount}</strong> para TE
            {stats.dtCount > 0    && <> + <strong>{stats.dtCount}</strong> DT</>}
            {stats.vitriCount > 0 && <> + <strong>{stats.vitriCount}</strong> vitrificados</>}
            {stats.descCount > 0  && <> + <strong>{stats.descCount}</strong> descartados</>}.
          </p>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          TABELA 3 — Descarte
          Espelha a de congelamento: descarte é afirmação sobre embriões
          concretos, e por isso pede a mesma avaliação por estágio.
      ═══════════════════════════════════════════════════════════════════ */}
      <div>
        <h4 className="font-semibold text-slate-800 text-sm mb-2">Descarte</h4>
        <div className="overflow-x-auto sm:overflow-visible sm:w-max sm:min-w-full rounded-lg border border-slate-200">
          <table className="w-full text-xs border-collapse min-w-max">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap border-r border-slate-200">Doadora × Touro</th>
                {FIV_DESC_STAGE_FIELDS.map(({ label }) => (
                  <th key={label} className="px-2 py-1.5 text-center font-semibold w-16 text-slate-700">{label}</th>
                ))}
                <th className="px-2 py-1.5 text-center font-semibold w-16 border-r border-slate-200 text-slate-700">Total</th>
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">Motivo (opcional)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {donors.map((donor, di) => {
                const descTotal = getDescStageSum(donor);
                return (
                  <tr key={donor.id} className="hover:bg-slate-50">
                    <td className="px-2 py-1 border-r border-slate-100">
                      <span className="font-medium text-slate-800">{donor.donor_identifier || '—'}</span>
                      {donor.bull && <span className="text-slate-400 ml-1">× {donor.bull}</span>}
                    </td>
                    {FIV_DESC_STAGE_FIELDS.map(({ key }) => (
                      <td key={key} className="px-1.5 py-1">
                        <NumInput value={donor[key]}
                          onChange={(v) => handleDonorChange(di, key, v)}
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1 text-center border-r border-slate-100">
                      <span className={`font-semibold ${descTotal > 0 ? 'text-slate-700' : 'text-slate-300'}`}>
                        {descTotal || '—'}
                      </span>
                    </td>
                    <td className="px-2 py-1">
                      <input type="text" placeholder="Degenerado, contaminado…"
                        className="w-full min-w-[160px] px-1.5 py-0.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-slate-400"
                        value={donor.dest_desc_obs || ''}
                        onChange={(e) => handleDonorChange(di, 'dest_desc_obs', e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Resumo ── */}
      {stats.totalEmbryos > 0 && (
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-sm text-slate-700">
            <strong>{stats.totalEmbryos} embriões</strong> produzidos —{' '}
            <strong>{stats.freshCount} TE</strong>,{' '}
            <strong>{stats.dtCount} DT</strong>,{' '}
            <strong>{stats.vitriCount} Vitri</strong>
            {donors.reduce((s, d) => s + getDonorDescarte(d), 0) > 0 && (
              <>, <strong>{donors.reduce((s, d) => s + getDonorDescarte(d), 0)} descarte</strong></>
            )}
          </p>
        </div>
      )}

      {/* ── Feedback de save ── */}
      {saveStatus === 'error' && (
        <div className="p-3 bg-slate-50 border border-slate-300 rounded-lg text-sm text-slate-700">
          <strong>{MARK.WARN} Erro ao salvar FIV.</strong> Veja o console (F12) para detalhes.
          {saveError && <p className="mt-1 text-xs font-mono">{saveError}</p>}
        </div>
      )}

      {/* ── Concluído ── */}
      <div className="flex items-center gap-2">
        <input type="checkbox" id="fiv_completed"
          checked={fivData.completed || false}
          onChange={(e) => setFivData(prev => ({ ...prev, completed: e.target.checked }))}
          className="form-checkbox h-5 w-5 text-blue-600"
        />
        <label htmlFor="fiv_completed" className="text-sm font-medium text-slate-700">
          Marcar sessão FIV como concluída
        </label>
      </div>

      <NumerarEmbrioesModal
        show={!!doadoraNumerando}
        serviceId={service?.id}
        donor={doadoraNumerando}
        onClose={() => setDoadoraNumerando(null)}
        // Refaz a carga da aba, não da página: mudar a fase de um embrião
        // altera as contagens do doador, e a tela precisa refletir isso.
        onSaved={() => setIsInitialized(false)}
      />

    </div>
  );
});

export default FIVTab;
